import { Helmet } from "react-helmet-async";
import { useI18n, languages } from "@/i18n";
import { localizedHead } from "@/i18n/headTranslations";

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  structuredData?: any;
  noindex?: boolean;
  breadcrumbs?: BreadcrumbItem[];
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
    tags?: string[];
  };
}

export const SEO = ({ 
  title, 
  description,
  keywords = "luxury smart toilets, premium bidet systems, intelligent sanitary systems, smart bathroom fixtures, bathroom vanities, shower enclosures, bathtubs, toilets, faucets, bathroom accessories, LED bathroom mirrors, heated towel racks, rain showers, walk-in tubs, wall-hung toilets, water-saving toilets, thermostatic mixers, flush valves, bathroom cabinets, towel warmers, bathroom lighting, grab bars, shower doors, shower bases, hand showers, toilet seats, urinals, plumbing supplies, soap dispensers, bathroom shelves, vanity tops, bathroom fans, pedestal sinks, freestanding tubs, bathroom safety products, custom shower doors, shower panels, bathroom tiles, backlit mirrors, European wellness technology, luxury home automation, intelligent bathroom systems",
  image = "/images/og-default.jpg",
  url,
  type = "website",
  structuredData,
  breadcrumbs,
  article,
  noindex = false
}: SEOProps) => {
  const { language, t } = useI18n();
  title = title ?? t.meta.defaultTitle;
  description = description ?? t.meta.defaultDescription;
  // Avoid double branding when a localized title already carries the brand.
  const hasBrand = /AVT|Aureum VII/i.test(title);
  const fullTitle = hasBrand ? title : `${title} | AVT`;
  
  // Normalize canonical URL to avoid duplicates
  const normalizeUrl = (urlPath?: string): string => {
    if (!urlPath) return '/';
    
    // If full URL provided, extract pathname
    if (urlPath.startsWith('http')) {
      urlPath = new URL(urlPath).pathname;
    }
    
    // Strip an existing language prefix (e.g. /en/foo -> /foo) so we always
    // build a language-agnostic path that the hreflang/canonical logic can prefix.
    urlPath = urlPath.replace(/^\/([a-z]{2})(?=\/|$)/, '') || '/';
    
    // Remove trailing slash except for homepage
    if (urlPath.length > 1 && urlPath.endsWith('/')) {
      urlPath = urlPath.slice(0, -1);
    }
    
    // Ensure starts with /
    if (!urlPath.startsWith('/')) {
      urlPath = '/' + urlPath;
    }
    
    return urlPath;
  };
  
  // Fall back to the live pathname so pages that don't pass `url` still get a
  // self-referencing canonical instead of defaulting to the homepage.
  const resolvedUrl =
    url ?? (typeof window !== 'undefined' ? window.location.pathname : '/');
  const canonicalUrl = normalizeUrl(resolvedUrl);
  const pathSuffix = canonicalUrl === '/' ? '' : canonicalUrl;
  const baseUrl = 'https://www.aureum7trading.com';
  const fullUrl = `${baseUrl}/${language}${pathSuffix}`;
  const fullImage = image?.startsWith('http') ? image : `${baseUrl}${image}`;
  const currentLangInfo = languages.find(l => l.code === language);
  const head = localizedHead(language, canonicalUrl, { title: fullTitle, description });
  
  return (
    <Helmet>
      <title>{head.title}</title>
      <meta name="description" content={head.description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={fullUrl} />
      
      {/* Hreflang alternates for international SEO */}
      {languages.map((lang) => (
        <link
          key={lang.code}
          rel="alternate"
          hrefLang={lang.locale}
          href={`${baseUrl}/${lang.code}${pathSuffix}`}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${baseUrl}/en${pathSuffix}`} />
      
      {/* Enhanced SEO Meta Tags */}
      <meta name="robots" content={noindex ? "noindex, follow" : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"} />
      <meta name="googlebot" content={noindex ? "noindex, follow" : "index, follow"} />
      <meta name="bingbot" content={noindex ? "noindex, follow" : "index, follow"} />
      <meta name="google" content="notranslate" />
      <meta name="yandex" content="index, follow" />
      <meta name="duckduckbot" content="index, follow" />
      <meta name="slurp" content="index, follow" />
      <meta name="msvalidate.01" content="BING_VERIFICATION_CODE" />
      <meta name="format-detection" content="telephone=yes" />
      <meta name="geo.region" content="ES-AN" />
      <meta name="geo.placename" content="Marbella" />
      <meta name="geo.position" content="36.5101;-4.8850" />
      <meta name="ICBM" content="36.5101, -4.8850" />
      
      {/* Industry & Category Tags */}
      <meta name="industry" content="Bathroom Fixtures, Smart Home Technology, Luxury Sanitary Systems" />
      <meta name="category" content="Smart Toilets, Bidets, Bathroom Vanities, Shower Enclosures, Bathtubs" />
      
      {/* Business Information */}
      <meta name="contact" content="info@aureum7trading.com" />
      <meta name="copyright" content="Aureum VII Trading" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      <meta name="distribution" content="global" />
      <meta name="rating" content="general" />
      
      {/* Open Graph Enhanced */}
      <meta property="og:title" content={head.title} />
      <meta property="og:description" content={head.description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`${title} - Luxury Smart Toilet Systems`} />
      <meta property="og:site_name" content="Aureum VII Trading" />
      <meta property="og:locale" content={(currentLangInfo?.locale || 'en-GB').replace('-', '_')} />
      {languages.filter(l => l.code !== language).map(l => (
        <meta key={l.code} property="og:locale:alternate" content={l.locale.replace('-', '_')} />
      ))}
      
      {/* Article specific Open Graph */}
      {article && type === "article" && (
        <>
          <meta property="article:published_time" content={article.publishedTime} />
          <meta property="article:modified_time" content={article.modifiedTime} />
          <meta property="article:author" content={article.author} />
          <meta property="article:section" content={article.section} />
          {article.tags?.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
        </>
      )}
      
      {/* Twitter Card Enhanced */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={head.title} />
      <meta name="twitter:description" content={head.description} />
      <meta name="twitter:image" content={fullImage} />
      <meta name="twitter:image:alt" content={`${title} - Luxury Smart Toilet Systems`} />
      <meta name="twitter:site" content="@AureumVII" />
      <meta name="twitter:creator" content="@AureumVII" />
      
      {/* Additional Performance and Mobile Tags */}
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="msapplication-TileColor" content="#0a0f1a" />
      <meta name="msapplication-config" content="/browserconfig.xml" />
      
      {/* Preconnect for performance */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="dns-prefetch" href="//www.google-analytics.com" />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
      
      {/* Breadcrumb Structured Data */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": breadcrumbs.map((item, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "name": item.name,
              "item": `https://www.aureum7trading.com${item.url}`
            }))
          })}
        </script>
      )}
      
      {/* Default fallback structured data — Article for blog posts, LocalBusiness otherwise */}
      {!structuredData && type === "article" && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": title,
            "description": description,
            "image": fullImage,
            "url": fullUrl,
            "datePublished": article?.publishedTime,
            "dateModified": article?.modifiedTime || article?.publishedTime,
            "author": {
              "@type": "Organization",
              "name": article?.author || "Aureum VII Trading"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Aureum VII Trading",
              "logo": {
                "@type": "ImageObject",
                "url": "https://www.aureum7trading.com/images/og-default.jpg"
              }
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": fullUrl
            },
            ...(article?.section ? { "articleSection": article.section } : {}),
            ...(article?.tags?.length ? { "keywords": article.tags.join(", ") } : {})
          })}
        </script>
      )}

      {/* Default Organization Structured Data — no street address (owner rule), area served: Spain */}
      {!structuredData && type !== "article" && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "@id": "https://www.aureum7trading.com/#organization",
            "name": "Aureum VII Trading",
            "alternateName": "AVT",
            "url": "https://www.aureum7trading.com",
            "logo": "https://www.aureum7trading.com/images/og-default.jpg",
            "description": "Marbella-based supplier of smart toilets and bathroom pieces. Product information and trade enquiries are discussed individually.",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Marbella",
              "addressCountry": "ES"
            },
            "areaServed": {
              "@type": "Country",
              "name": "Spain"
            },
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+34642278939",
              "contactType": "customer service",
              "email": "info@aureum7trading.com",
              "availableLanguage": ["English", "Spanish", "French"]
            }
          })}
        </script>
      )}
    </Helmet>
  );
};